import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import type * as zod from 'zod';

import { debug, error, info, namespace, warn } from '@sb/log';
import { RecorderStatus } from '@sb/messenger/auto_generated/messenger/echo_bot_service';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { Timer, wait } from '@sb/utilities';

import { FailureKind } from '../FailureKind';
import type { RoutineContext } from '../RoutineContext';
import type { RoutineFrame } from '../RoutineFrame';
import type { RoutineRunningPauseDetails } from '../RoutineRunnerState';
import type { StepFailure } from '../types';
import type { Conditional } from '../types/expressions/Conditional';

import type { RoutineStepConfiguration } from './RoutineStepConfiguration';
import type { TaggedRoutineStepVariables } from './RoutineStepVariables';
import type { StepClass } from './StepClass';
import type { StepKind } from './StepKind';
import { StepRegistry } from './StepRegistry';
import type { StepValidationMessage } from './StepValidationMessage';

const ns = namespace('routine.runner');

type Config<Kind extends StepKind> = RoutineStepConfiguration & {
  stepKind: Kind;
};

type StepState = 'playing' | 'paused' | 'stopped';

type StepTimers = {
  self: Timer;
  full: Timer;
};

/**
 * Arguments passed to `StepPlayArguments.playSubSteps`.
 *
 * `substeps` is an optional per-call override: when present, those steps are
 * played in place of `step.substeps`. This lets steps like `CallSubroutine`
 * thread a freshly-constructed per-call substep tree through the dispatch path
 * without mutating shared state on the step instance. When absent, dispatch
 * falls back to the spec-bound `step.substeps` (e.g., `Loop`, `If`).
 */
export interface PlaySubStepsArguments {
  pushMode?: boolean;
  breakOutOfLoop?: () => void;
  continueLoop?: () => void;
  hasBeenPreempted?: () => boolean;
  skipSettingCurrentStepID?: boolean;
  substeps?: Array<Step>;
}

export interface StepPlayArguments {
  pushMode?: boolean;
  hasPushed?: boolean;
  breakOutOfLoop?: () => void;
  continueLoop?: () => void;
  hasBeenPreempted: () => boolean;
  hasEncounteredStartStep: () => boolean;
  fail: (failure: StepFailure) => Promise<void>;
  playSubSteps: (args?: PlaySubStepsArguments) => Promise<void>;
  pauseRoutine: (pauseDetails: RoutineRunningPauseDetails) => Promise<void>;
}

export interface StepAction {
  fail: (failure: StepFailure) => Promise<void>;
  reason?: string;
}

export type MonitoringHandlers = {
  onTriggered?: (
    callbacks: {
      isStopped: () => boolean;
      stopMonitoring: () => void;
    },
    context?: any,
  ) => void | Promise<void>;
  onError?: (
    err: unknown,
    callbacks: { isStopped: () => boolean; stopMonitoring: () => void },
  ) => void | Promise<void>;
};

export const DEFAULT_SELF_TIMEOUT_BUFFER_MS = 250;

/**
 * An abstract class that each routine substep implements in order to validate and run.
 *
 * The template arguments are only necessary for internal consistency to ensure the
 * subclass Steps are implemented correctly. The Step static class treats Steps like these
 * template parameters are `any`.
 */
export default abstract class Step<
  // Args: The type of the arguments for the step. The type is based on the Kind and the schema.
  // Variables: The type of the variable state for the step as stored in the routine context.
  Args extends object = object,
  Variables extends object = object,
> {
  /**
   * Recursively constructs a step from its JSON configuration.
   *
   * Runs Zod arg validation and caches the parsed args on the frame's load
   * session (`frame.loadedRoutine.parsedArgsByStepId`, keyed by
   * `routineId:stepId`) so that subsequent per-call constructions of the same
   * step (via `Step.constructStepFromCache`) can skip the Zod parse.
   *
   * Basically a factory function.
   *
   * @param configuration     The routine step configuration as specified in the Routine JSON
   * @param routineContext    The routine's full context including other steps' variables,
   *                          the robot, the camera, and more.
   */
  public static constructStep<Kind extends StepKind>(
    config: Config<Kind> & { suppressed?: boolean },
    routineContext: RoutineContext,
    frame: RoutineFrame,
    // Duplicate-id detection is scoped to one construction walk: the walk's
    // root creates the set and the recursion threads it. Step ids are only
    // unique within a routine, so each spec walks against its own set.
    seenStepIds: Set<string> = new Set<string>(),
  ):
    | { errors: Array<StepValidationMessage> }
    | { step: Step<Config<Kind>['args'], object> } {
    const { id, stepKind, args, steps } = config;

    const Klass = (
      StepRegistry.stepConstructors as Record<
        string,
        StepClass<any, any> | undefined
      >
    )[stepKind as unknown as string];

    if (!Klass) {
      return {
        errors: [
          {
            id,
            messages: [`No step kind "${stepKind}" exists`],
          },
        ],
      };
    }

    // Combined errors for the step and its substeps.
    // Without this, errors for substeps won't get propagated down to the flat
    // array that eventually gets returned.
    let nestedErrors: Array<StepValidationMessage> = [];

    // errors corresponding specifically to this step
    const errorMessages: Array<string | zod.ZodIssue> = [];

    if (seenStepIds.has(id)) {
      errorMessages.push(`Duplicate step ID ${id}`);
    } else {
      seenStepIds.add(id);
    }

    const substeps: Array<Step> = [];

    // if substeps have been specified
    if (steps) {
      if (steps.length === 0 && Klass.areSubstepsRequired) {
        errorMessages.push(`"${stepKind}" step must have substeps`);
      } else {
        steps.forEach((step) => {
          const value = Step.constructStep(
            step,
            routineContext,
            frame,
            seenStepIds,
          );

          if ('errors' in value) {
            nestedErrors = [...nestedErrors, ...value.errors];
          } else {
            substeps.push(value.step);
          }
        });
      }
    } else if (Klass.areSubstepsRequired) {
      errorMessages.push(`"${stepKind}" step must have substeps`);
    }

    const argsValidationResult = Klass.Arguments.safeParse(args);

    if (
      !argsValidationResult.success ||
      nestedErrors.length + errorMessages.length > 0
    ) {
      if (!argsValidationResult.success) {
        for (const issue of argsValidationResult.error.issues) {
          errorMessages.push(issue);
        }
      }

      if (errorMessages.length > 0) {
        nestedErrors.push({
          id,
          // TypeScript doesn't believe the check above
          messages: errorMessages as [string, ...string[]],
        });
      }

      return { errors: nestedErrors };
    }

    // Cache the parsed args for per-call construction in
    // `Step.constructStepFromCache`. Step ids are only unique WITHIN a
    // routine — the loaded routine and a subroutine (or two subroutines) can
    // legitimately share an id (routine duplication preserves ids) — so the
    // key must be scoped by the owning routine or a colliding id would serve
    // another routine's args.
    frame.loadedRoutine.parsedArgsByStepId.set(
      `${frame.routineId}:${id}`,
      argsValidationResult.data,
    );

    return Step._constructStepInstance<Kind>(
      config,
      Klass,
      argsValidationResult.data,
      substeps,
      routineContext,
      frame,
    );
  }

  /**
   * Recursively constructs a step from its JSON configuration without
   * re-running Zod arg validation, by reading already-parsed args from
   * the frame's load session (`frame.loadedRoutine.parsedArgsByStepId`).
   *
   * Used at per-call construction time (e.g. from `CallSubroutineStep._play`)
   * after the loaded-routine `loadRoutine` walk has already populated the cache via
   * `Step.constructStep`.
   *
   * Skips the per-walk duplicate-id guard (`loadRoutine`'s walks already
   * validated the spec once; re-constructing the same ids per call is by
   * design) but still re-runs cheap structural checks (Klass lookup,
   * substep arity).
   */
  public static constructStepFromCache<Kind extends StepKind>(
    config: Config<Kind> & { suppressed?: boolean },
    routineContext: RoutineContext,
    frame: RoutineFrame,
  ):
    | { errors: Array<StepValidationMessage> }
    | { step: Step<Config<Kind>['args'], object> } {
    const { id, stepKind, steps } = config;

    const Klass = (
      StepRegistry.stepConstructors as Record<
        string,
        StepClass<any, any> | undefined
      >
    )[stepKind as unknown as string];

    if (!Klass) {
      return {
        errors: [
          {
            id,
            messages: [`No step kind "${stepKind}" exists`],
          },
        ],
      };
    }

    // Scoped by the owning routine, matching the `constructStep` write — a
    // bare id could collide with another routine's step (ids are only unique
    // within a routine).
    const parsedArgsCacheKey = `${frame.routineId}:${id}`;

    if (!frame.loadedRoutine.parsedArgsByStepId.has(parsedArgsCacheKey)) {
      return {
        errors: [
          {
            id,
            messages: [
              `No cached parsed arguments for step ID "${id}" in routine ` +
                `"${frame.routineId}"; constructStepFromCache requires that ` +
                `constructStep was called for this step earlier (e.g. during ` +
                `loadRoutine).`,
            ],
          },
        ],
      };
    }

    const parsedArgs =
      frame.loadedRoutine.parsedArgsByStepId.get(parsedArgsCacheKey);

    let nestedErrors: Array<StepValidationMessage> = [];
    const errorMessages: Array<string | zod.ZodIssue> = [];

    const substeps: Array<Step> = [];

    if (steps) {
      if (steps.length === 0 && Klass.areSubstepsRequired) {
        errorMessages.push(`"${stepKind}" step must have substeps`);
      } else {
        steps.forEach((step) => {
          const value = Step.constructStepFromCache(
            step,
            routineContext,
            frame,
          );

          if ('errors' in value) {
            nestedErrors = [...nestedErrors, ...value.errors];
          } else {
            substeps.push(value.step);
          }
        });
      }
    } else if (Klass.areSubstepsRequired) {
      errorMessages.push(`"${stepKind}" step must have substeps`);
    }

    if (nestedErrors.length + errorMessages.length > 0) {
      if (errorMessages.length > 0) {
        nestedErrors.push({
          id,
          messages: errorMessages as [string, ...string[]],
        });
      }

      return { errors: nestedErrors };
    }

    return Step._constructStepInstance<Kind>(
      config,
      Klass,
      parsedArgs,
      substeps,
      routineContext,
      frame,
    );
  }

  /**
   * Shared instantiation tail used by both `Step.constructStep` and
   * `Step.constructStepFromCache` once parsed args and constructed substeps
   * are in hand.
   *
   * `step.args` holds the *raw* (unparsed) args from the configuration,
   * while the parsed args are passed to the Step constructor.
   */
  private static _constructStepInstance<Kind extends StepKind>(
    {
      id,
      args,
      suppressed,
      stepKind,
      description,
    }: Config<Kind> & { suppressed?: boolean },
    Klass: StepClass<any, any>,
    parsedArgs: unknown,
    substeps: Array<Step>,
    routineContext: RoutineContext,
    frame: RoutineFrame,
  ):
    | { errors: Array<StepValidationMessage> }
    | { step: Step<Config<Kind>['args'], object> } {
    const step = new Klass({
      args: parsedArgs,
      routineContext,
      id,
    });

    if (step.substeps) {
      step.substeps = substeps;

      for (const substep of step.substeps) {
        substep.parentSteps = [...step.parentSteps, step];
      }
    } else if (substeps.length > 0) {
      return {
        errors: [
          {
            id,
            messages: [`"${stepKind}" step cannot have substeps`],
          },
        ],
      };
    }

    step.id = id;
    step.suppressed = suppressed ?? false;
    step.description = description;
    step.args = args;
    step.routineContext = routineContext;
    step.routineFrame = frame;

    step.resetVariables({ excludeSubSteps: true });

    return { step: step as Step<Config<Kind>['args'], object> };
  }

  public parentSteps: Array<Step<object, object>> = [];

  /**
   * The substeps of the Step.
   *
   * If substeps are not allowed, subclasses of Step should leave this empty
   * and a validation error will occur if the frontend tries to create that kind of
   * step with substeps.
   */
  public substeps?: Array<Step<object, object>>;

  /**
   * The ID of this step, used by other steps' configurations, the database, and more.
   *
   * Implementation note: This gets overridden in constructStep immediately after construction.
   * Steps cannot access their own id in their constructor.
   */
  public id: string = '';

  /** When true, this step is excluded from execution. */
  public suppressed: boolean = false;

  /** The user-facing name of the step from its configuration, if set. */
  public description?: string;

  public timers: StepTimers = { self: new Timer(), full: new Timer() };

  /**
   * Wall-clock timestamp (ms) when this step started playing.
   * Set at the beginning of play(), useful for calculating absolute timing.
   */
  public playStartTime: number = 0;

  /**
   * History of play() invocations for this step.
   * Each entry captures timing from one play() call — needed because loop
   * iterations reuse the same substep objects and overwrite playStartTime/timers.
   */
  public playHistory: Array<{
    playStartTime: number;
    fullTimeMs: number;
    selfTimeMs: number;
  }> = [];

  private isPlayingSubSteps = false;

  /**
   * The routine context, used by steps for managing output variables.
   *
   * This gets populated *after* construction, so subclasses should not use it during
   * the constructor.
   *
   * If a step needs the routine context during construction, the constructor can get it
   * from the arguments passed to it.
   */
  protected routineContext: RoutineContext = null as any;

  /**
   * The scope this step executes in. Set by `_constructStepInstance`
   * immediately after construction; every Step instance has a frame
   * (canonical instances use a per-subroutine canonical frame from
   * `RoutineRunner.loadRoutine`; per-call instances use the fresh frame
   * from `CallSubroutineStep._play`).
   */
  public routineFrame!: RoutineFrame;

  protected setVariable<K extends keyof Variables>(
    variable: K,
    property: Variables[K],
  ) {
    this.routineFrame.setVariable(this.id, {
      ...this.variables,
      [variable]: property,
      stepKind: this.getStepKind(),
    } as TaggedRoutineStepVariables);
  }

  /**
   * Every time we set the variables, we want to set them on the routine context.
   * Therefore, `variables` is a virtual property.
   *
   * These protected methods are only used by substeps and therefore do not need
   * to include `stepKind` as that can be deduced.
   */
  protected set variables(variables: Variables) {
    // @rivertam: I spent a lot of time trying to avoid this cast but TypeScript
    // can't seem to figure out that this is always the right type. I think it
    // might be because technically Kind can be a union type so you could technically
    // write like a `Step<'If' | 'Wait'>` and then pass back something like
    // `{ stepKind: 'If', ...WaitStepVariables }` and this would be an invalid
    // TaggedRoutineStepVariables.
    this.routineFrame.setVariable(this.id, {
      ...variables,
      stepKind: this.getStepKind(),
    } as TaggedRoutineStepVariables);
  }

  /**
   * There's a single source of truth for variables, so we can just reference the context's
   * copy of this step's variables.
   *
   * These protected methods are only used by substeps and therefore do not need
   * to include `stepKind`.
   */
  protected get variables(): Variables {
    if (this.isInitializingVariableState) {
      throw new Error(
        'Steps cannot get `this.variables` during `initializeVariableState` as it is essentially recursive',
      );
    }

    const variables: Partial<TaggedRoutineStepVariables> = {
      ...this.routineFrame.getVariable(this.id),
    };

    if (variables.stepKind !== this.getStepKind()) {
      // this should never happen; programming error where we stored the wrong
      // variables or didn't clear them at the right time or something.
      throw new Error(
        `Step variables were of the wrong step kind ("${
          variables.stepKind
        }" vs. expected "${this.getStepKind()}")`,
      );
    }

    delete variables.stepKind;

    return variables as any; // the types are more complicated than they're worth
  }

  /**
   * Use to get variables in the initializeVariableState methods when needed,
   * it will contain the variables data passed in the startConditions when loading the routine
   */
  protected get variablesForInitialization(): Record<string, any> {
    // Read from this step's own frame. A sub-resident step's frame is
    // its sub's frame, not the loaded routine's, so reading via
    // `routineContext.getAllVariables()` (loaded routine + globals only)
    // would miss it.
    return this.routineFrame.variableValues[this.id] ?? {};
  }

  /**
   * The arguments for a step.
   *
   * This gets populated *after* construction, so subclasses should not use it during
   * the constructor.
   *
   * If a step needs the args during construction, the constructor can get it
   * from the arguments passed to it.
   */
  public args: Args = null as any;

  public getStepKind(): StepKind {
    const entry = Object.entries(StepRegistry.stepConstructors).find(
      // not sure why this doesn't type-check with a new () operator
      ([_stepKind, stepClass]) => this instanceof (stepClass as any),
    );

    if (!entry) {
      throw new Error(
        `Step class ${this.constructor.name} needs to call Step.registerStepKind`,
      );
    }

    // not sure why the Object.entries(Step.stepConstructors) doesn't
    // already give back StepKinds
    return entry[0] as StepKind;
  }

  /**
   * 1-based DFS pre-order index of this step within its routine's tree.
   * The "step number" the failure-attribution UI displays. Computed by
   * DFS-walking the routine's tree from its top-level steps (stored on
   * the frame) until this instance is found. Returns 0 if the step
   * isn't found in its frame's tree (shouldn't happen in practice,
   * defensive).
   *
   * Walked on demand rather than stored as a field because pre-order
   * numbering needs the full tree, but `constructStep` builds in
   * post-order.
   */
  public getStepPosition(): number {
    let position = 0;
    let found = false;

    const walk = (s: Step): void => {
      if (found) return;
      position += 1;

      if (s === this) {
        found = true;

        return;
      }

      s.substeps?.forEach(walk);
    };

    for (const root of this.routineFrame.routineSteps) {
      if (found) break;
      walk(root);
    }

    return found ? position : 0;
  }

  /**
   * True when this step is directly suppressed, or any ancestor is suppressed.
   * Single source of truth for "will this step run?" in RoutineRunner.playSteps
   * and anywhere a decorator filters its own substeps (e.g. MoveArmToV2).
   */
  public isEffectivelySuppressed(): boolean {
    return this.suppressed || this.parentSteps.some((p) => p.suppressed);
  }

  public hasSelfTimeout(): boolean {
    return true;
  }

  /**
   * Whether this step is currently accepting `RoutineRunner#confirmStep`
   * input. Steps that pause the routine on operator input (WaitForConfirmation,
   * Antigravity, CreateRecording's label prompt) override this; the runner
   * consults it instead of knowing step types. The default refuses, so a
   * confirm aimed at an ordinary step is never a resume backdoor around
   * whatever pause the operator is really looking at.
   */
  public acceptsConfirmation(): boolean {
    return false;
  }

  /**
   * Whether the runner must pass `resumeRoutinePreChecks` BEFORE `confirm` is
   * applied. Set by steps whose `confirm` irreversibly releases the step — if
   * the subsequent resume failed its prechecks, the step would already be
   * running (e.g. uploading a recording) while the routine stays paused.
   */
  public readonly confirmationNeedsResumePrechecks: boolean = false;

  /**
   * Receive `RoutineRunner#confirmStep` input. Only called after
   * `acceptsConfirmation()` returns true; the default exists so the runner
   * can dispatch polymorphically without per-step casts.
   */
  public confirm(_input?: string): void {
    throw new Error(
      `${this.constructor.name} does not accept confirmation input.`,
    );
  }

  public getSelfTimeout(): number {
    return DEFAULT_SELF_TIMEOUT_BUFFER_MS;
  }

  public getSelfTimeoutWarning(): number {
    return this.getSelfTimeout() * 0.75;
  }

  /**
   * If we try to call `this.variables` during `initializeVariableState`, a relatively confusing
   * error message pops up, so this allows us to more nicely inform the programmer they
   * made a mistake.
   */
  private isInitializingVariableState = false;

  /**
   * Called by the [[RoutineRunner]] to initialize the variables for this Step.
   * The implementation should at some point do: `this.variables = {...}`
   */
  protected abstract initializeVariableState(): void;

  private stepState$ = new BehaviorSubject<StepState>('stopped');

  /**
   * Registry of active monitoring cleanup callbacks so we can stop them all on step stop.
   * @internal
   */
  private monitoringCleanups: Set<() => void> = new Set();

  /**
   * Run a step.
   *
   * This should resolve when the step has completed and the next step is good
   * to go. Make sure not to implement it in a way where it completes when `pause()`
   * is called.
   *
   * If this function rejects (throws an error), this will cause the Failure kind of
   * the routine runner to become Internal, indicating a programming error whose specifics
   * we don't want to reveal to the user.
   *
   * The error will be logged and (TODO?) reported to some bug-catching tool, while the
   * `failureKind` and `failureReason` will be reported to the user/remote control for
   * recovery.
   */
  protected async _play(_args: StepPlayArguments): Promise<void> {
    // empty
  }

  public async play(args: StepPlayArguments): Promise<void> {
    this.playStartTime = Date.now();

    this.timers.full.restart();
    this.timers.self.restart();

    this.stepState$.next('playing');

    await this._play({
      ...args,
      playSubSteps: async (subArgs) => {
        this.isPlayingSubSteps = true;
        this.timers.self.pause();
        await args.playSubSteps(subArgs);
        this.timers.self.play();
        this.isPlayingSubSteps = false;
      },
    });

    this.timers.self.pause();
    this.timers.full.pause();

    this.playHistory.push({
      playStartTime: this.playStartTime,
      fullTimeMs: this.timers.full.getTime(),
      selfTimeMs: this.timers.self.getTime(),
    });

    const selfTime = this.timers.self.getTime();
    const fullTime = this.timers.full.getTime();

    info(ns`play.time`, 'Step times', {
      selfTime,
      fullTime,
      stepId: this.id,
      stepKind: this.getStepKind(),
    });

    if (this.hasSelfTimeout()) {
      if (selfTime > this.getSelfTimeout()) {
        error(ns`play.time.exceeded`, 'Step self time exceeded threshold', {
          selfTime,
          fullTime,
          stepId: this.id,
          stepKind: this.getStepKind(),
        });
      } else if (selfTime > this.getSelfTimeoutWarning()) {
        warn(ns`play.time.high`, 'Step self time nearing threshold', {
          selfTime,
          fullTime,
          stepId: this.id,
          stepKind: this.getStepKind(),
        });
      }
    }
  }

  /**
   * Pause the step
   */
  protected _pause(_args: StepAction): void | Promise<void> {
    // empty
  }

  public async pause(args: StepAction): Promise<void> {
    this.stepState$.next('paused');

    await this._pause(args);

    if (!this.isPlayingSubSteps) {
      this.timers.self.pause();
    }
  }

  /**
   * Resume the step
   *
   * Async-capable, matching `_pause` and `_stop`: a step whose resume does
   * real work (CodeBlock signals its container) must be awaitable, or its
   * promise floats and can be reordered against the matching pause.
   */
  protected _resume(_args: StepAction): void | Promise<void> {
    // empty
  }

  public async resume(args: StepAction): Promise<void> {
    if (!this.isPlayingSubSteps) {
      this.timers.self.play();
    }

    this.stepState$.next('playing');

    await this._resume(args);
  }

  /**
   * Stop the step (cleans up data and running processes)
   */
  protected _stop(_args: StepAction): void | Promise<void> {
    // empty
  }

  public async stop(args: StepAction): Promise<void> {
    this.stepState$.next('stopped');

    // Cleanup any active monitoring loops
    for (const cleanup of this.monitoringCleanups) {
      try {
        cleanup();
      } catch {}
    }

    this.monitoringCleanups.clear();

    await this._stop(args);
  }

  /**
   * Called once before the routine execution begins.
   * All steps' setup() methods are called before any step's play() method.
   */
  protected async _setup(_args: StepAction): Promise<void> {
    // empty by default, steps can override
  }

  public async setup(args: StepAction): Promise<void> {
    await this._setup(args);
  }

  /**
   * Called once after the routine execution completes (successfully or with failure).
   * All steps' teardown() methods are called after all steps have finished.
   */
  protected async _teardown(_args: StepAction): Promise<void> {
    // empty by default, steps can override
  }

  public async teardown(args: StepAction): Promise<void> {
    await this._teardown(args);
  }

  /**
   * Called when a step is cleaned up after leaving the running list.
   * This is distinct from stop(), which is used for preemption/cancel.
   * This is distinct from teardown(), which is used for cleanup after the routine execution completes.
   * It also only is called for steps that are actually executed, not for all steps
   */
  protected async _onCleanup(_args: StepAction): Promise<void> {
    // empty by default, steps can override
  }

  public async onCleanup(args: StepAction): Promise<void> {
    await this._onCleanup(args);
  }

  /**
   * return a promise which will be unresolved while paused, and resolved with true if stopped.
   */
  protected async shouldStopPlaying() {
    const stepState = await this.stepState$
      .pipe(first((state) => state !== 'paused'))
      .toPromise();

    return stepState === 'stopped';
  }

  /**
   * Generic polling-based monitoring utility.
   * Starts a background loop that evaluates a predicate at a set interval and triggers handlers.
   * Returns a cleanup function to stop the monitoring early. All monitors are auto-stopped on Step.stop().
   * @param predicate - The predicate to monitor
   * @param handlers - The handlers to trigger when the predicate is met or an error occurs
   * @param options - Monitoring options (primarily pollIntervalMs)
   * @returns Cleanup function to stop monitoring manually
   */
  protected startMonitoring(
    predicate: () =>
      | { triggered: boolean; context?: any }
      | Promise<{ triggered: boolean; context?: any }>,
    handlers: MonitoringHandlers = {},
    options: { pollIntervalMs?: number } = {},
  ): () => void {
    const { pollIntervalMs = 100 } = options;
    let cancelled = false;

    const stopMonitoring = () => {
      cancelled = true;
      // remove self from registry
      this.monitoringCleanups.delete(stopMonitoring);
    };

    const callbacks = {
      isStopped: () => cancelled,
      stopMonitoring,
    } as const;

    // register cleanup
    this.monitoringCleanups.add(stopMonitoring);

    (async () => {
      while (!cancelled) {
        try {
          const { triggered: shouldTrigger, context } = await predicate();

          if (shouldTrigger) {
            if (!cancelled) {
              await handlers.onTriggered?.(callbacks, context);
            }

            break;
          }

          await wait(pollIntervalMs);
        } catch (err) {
          if (!cancelled) {
            await handlers.onError?.(err, callbacks);
          }

          break;
        }
      }
      // ensure cleanup after loop terminates

      stopMonitoring();
    })();

    return stopMonitoring;
  }

  /**
   * Conditional monitoring wrapper over startMonitoring, to exit step when condition is met.
   * @param condition - The conditional expression to monitor
   * @param onConditionMet - Callback to execute when the condition is met
   * @param onFailure - Callback to execute when condition evaluation fails
   * @param options - Monitoring options (primarily pollIntervalMs)
   * @returns Cleanup function to stop monitoring manually
   */
  protected startConditionalMonitoring(
    condition: Conditional,
    onConditionMet: MonitoringHandlers['onTriggered'],
    onFailure: (
      failure: StepFailure,
      callbacks: {
        isStopped: () => boolean;
        stopMonitoring: () => void;
      },
    ) => void | Promise<void>,
    options: { pollIntervalMs?: number } = {},
  ): () => void {
    if (!getFeatureFlagSync('enableLearnedModels', false)) {
      warn(
        ns`startConditionalMonitoring`,
        'Conditional monitoring is not supported when skills FF is not set.',
      );

      return () => {};
    }

    return this.startMonitoring(
      () =>
        this.routineFrame
          .evaluateConditional(condition)
          .then((result) => ({ triggered: result })),
      {
        onTriggered: onConditionMet,
        onError: async (err, callbacks) => {
          const errorObj = err as Error & { message?: string };

          await onFailure(
            {
              failure: {
                kind: FailureKind.InvalidRoutineLoadedFailure,
              },
              failureReason: errorObj?.message,
              error: err as any,
            },
            callbacks,
          );
        },
      },
      options,
    );
  }

  /**
   * Add a tag to the recording if recording is active.
   * Useful for marking significant events during step execution (e.g., 'inference', 'teleop').
   * @param tag - The tag to add to the recording
   */
  protected async conditionallyAddRecordingTag(tag: string): Promise<void> {
    const { echoBotConnector } = this.routineContext;

    if (!echoBotConnector) {
      info(
        ns`addRecordingTag`,
        'Echo bot connector not available, skipping tag addition',
      );

      return;
    }

    try {
      // Check if we're inside a CreateRecording or CreateDemonstration step
      // — both wrap their substeps in an active echo-bot recording so child
      // tag emitters should be allowed to push tags onto the active stream.
      const hasCreateRecordingParent = this.parentSteps.some(
        (p) =>
          p.getStepKind() === 'CreateRecording' ||
          p.getStepKind() === 'CreateDemonstration',
      );

      // Confirm that the recording is active
      const recorderState = await echoBotConnector.GetRecorderState({});

      const isEchoBotRecording =
        recorderState.status === RecorderStatus.RECORDING ||
        recorderState.status === RecorderStatus.INITIALIZING;

      // Require both conditions to be met
      if (!hasCreateRecordingParent && !isEchoBotRecording) {
        debug(
          ns`addRecordingTag`,
          'Recording is not active, skipping tag addition',
          {
            status: recorderState.status,
            hasCreateRecordingParent,
            tag,
          },
        );

        return;
      }

      const response = await echoBotConnector.AddTag({ tags: [tag] });

      if (!response.success || response.errors.length > 0) {
        warn(ns`addRecordingTag`, 'Failed to add tag to recording', {
          tag,
          success: response.success,
          errors: response.errors,
        });

        return;
      }

      info(ns`addRecordingTag`, 'Added tag to recording', {
        tag,
        status: recorderState.status,
      });
    } catch (err) {
      // Log error but don't fail the step (transport/network errors)
      warn(ns`addRecordingTag`, 'Failed to add tag to recording', {
        tag,
        error: err,
      });
    }
  }

  toJSON() {
    return {
      id: this.id,
      stepKind: this.getStepKind(),
      args: this.args,
      variables: this.variables,
    };
  }

  public resetVariables(args?: { excludeSubSteps: boolean }) {
    this.isInitializingVariableState = true;
    this.initializeVariableState();
    this.isInitializingVariableState = false;

    // Reset timing state for clean routine runs
    this.playStartTime = 0;
    this.playHistory = [];

    if (!args?.excludeSubSteps && this.substeps) {
      for (const substep of this.substeps) {
        substep.resetVariables();
      }
    }
  }
}
